import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useVerifyPasskeyMfa } from '@/hooks/useAuthMutations';
import { LoginResult } from '@vivport/backend/types';
import { AlertCircle, KeyRound, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Notice } from '@/components/ui/notice';

interface LoginMfaPasskeyProps {
  mfaToken: string;
  onSuccess: (result: LoginResult) => void | Promise<void>;
  onError?: (error: string) => void;
  onBack?: () => void;
  autoTrigger?: boolean;
}

export function LoginMfaPasskey({ mfaToken, onSuccess, onError, onBack, autoTrigger = false }: LoginMfaPasskeyProps) {
  const passkeyVerifyMutation = useVerifyPasskeyMfa();

  const handlePasskeyAuth = () => {
    passkeyVerifyMutation.mutate({ mfaToken }, {
      onSuccess,
      onError: (error) => onError?.(error.message),
    });
  };

  // Auto-trigger authentication if requested
  useEffect(() => {
    if (autoTrigger && !passkeyVerifyMutation.isPending && !passkeyVerifyMutation.isError) {
      handlePasskeyAuth();
    }
  }, [autoTrigger, passkeyVerifyMutation.isPending, passkeyVerifyMutation.isError]);

  const isPending = passkeyVerifyMutation.isPending;

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <div className="flex items-center justify-center mb-4">
          <KeyRound className="h-12 w-12" />
        </div>
        <CardTitle className="text-center">Passkey Authentication</CardTitle>
        <CardDescription className="text-center">
          {isPending 
            ? 'Follow the prompts to authenticate with your passkey'
            : 'Use your passkey to securely sign in'
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {(!autoTrigger || passkeyVerifyMutation.isError) && (
            <Button 
              onClick={handlePasskeyAuth}
              disabled={isPending}
              className="w-full"
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4 mr-2" />
                  {passkeyVerifyMutation.isError ? 'Try Again' : 'Authenticate with Passkey'}
                </>
              )}
            </Button>
          )}

          {autoTrigger && isPending && !passkeyVerifyMutation.isError && (
            <div className="flex items-center justify-center p-4 rounded-lg">
              <Loader2 className="h-6 w-6 mr-2 animate-spin" />
              <span>Authenticating...</span>
            </div>
          )}

          {passkeyVerifyMutation.isError && (
            <Notice tone="destructive" className="flex items-start space-x-2 rounded p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Authentication Failed</p>
                <p>{passkeyVerifyMutation.error?.message || 'Failed to authenticate with passkey. Please try again.'}</p>
              </div>
            </Notice>
          )}

          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              disabled={isPending}
              className="w-full"
            >
              Back to Login
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
} 